import type { VideoProgress } from '@/types/video';
import { useAuth } from '@clerk/nextjs';
import axios from 'axios';
import { useCallback, useRef } from 'react';
import { getApiBaseUrl } from '@/lib/apiUrl';

interface VideoInfo {
  url: string;
  duration: number;
  id: number;
}

interface VideoDurationResponse {
  totalDuration: number;
  videos: VideoInfo[];
}

export const useVideoService = () => {
  const { getToken } = useAuth();
  const tokenCache = useRef<{ token: string | null; timestamp: number }>({
    token: null,
    timestamp: 0,
  });
  const TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const apiCallCountRef = useRef(0);

  // Get cached token or fetch new one
  const getCachedToken = useCallback(async (): Promise<string> => {
    const now = Date.now();

    // Return cached token if it's still valid
    if (tokenCache.current.token && now - tokenCache.current.timestamp < TOKEN_CACHE_DURATION) {
      return tokenCache.current.token;
    }

    // Fetch new token and cache it
    const newToken = await getToken();
    if (!newToken) {
      throw new Error('Failed to get authentication token');
    }
    tokenCache.current = { token: newToken, timestamp: now };
    return newToken;
  }, [getToken]);

  // Retry function for API calls
  const retryApiCall = useCallback(
    async <T>(apiCall: (token: string) => Promise<T>, maxRetries: number = 2): Promise<T> => {
      apiCallCountRef.current += 1;
      const callId = apiCallCountRef.current;
      let lastError: Error = new Error('API call failed');

      console.log(`🔵 useVideoService: API call #${callId} starting`);

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          console.log(
            `🔵 useVideoService: API call #${callId} attempt ${attempt + 1}/${maxRetries + 1}`,
          );
          const token = await getCachedToken();
          const result = await apiCall(token);
          console.log(
            `🔵 useVideoService: API call #${callId} successful on attempt ${attempt + 1}`,
          );
          return result;
        } catch (error) {
          lastError = error as Error;

          // If it's a token-related error, clear cache and retry
          if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { status?: number } };
            if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
              console.warn(
                `🔵 useVideoService: Token expired (${axiosError.response?.status}), clearing cache and retrying...`,
                {
                  callId,
                  attempt: attempt + 1,
                  status: axiosError.response?.status,
                  timestamp: new Date().toISOString(),
                },
              );
              tokenCache.current = { token: null, timestamp: 0 };
              continue;
            }
          }

          const responseStatus =
            error && typeof error === 'object' && 'response' in error
              ? (error as { response?: { status?: number } }).response?.status
              : undefined;

          console.error(
            `🔵 useVideoService: API call #${callId} failed on attempt ${attempt + 1}`,
            {
              error: lastError.message,
              status: responseStatus,
              timestamp: new Date().toISOString(),
            },
          );

          // For other errors, don't retry
          break;
        }
      }

      console.error(
        `🔵 useVideoService: API call #${callId} failed after ${maxRetries + 1} attempts`,
      );
      throw lastError;
    },
    [getCachedToken],
  );

  const fetchTotalVideoDuration = useCallback(
    async (courseId: string): Promise<VideoDurationResponse> => {
      console.log(`🔵 useVideoService: fetchTotalVideoDuration called`, { courseId });
      return retryApiCall(async (token) => {
        const response = await axios.get<VideoDurationResponse>(
          `${getApiBaseUrl()}/videos/total-duration/${courseId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        return response.data;
      });
    },
    [retryApiCall],
  );

  const saveVideoProgress = useCallback(
    async (userId: string, videoUrl: string, progress: number): Promise<void> => {
      console.log(`🔵 useVideoService: saveVideoProgress called`, { userId, videoUrl, progress });
      return retryApiCall(async (token) => {
        await axios.post(
          `${getApiBaseUrl()}/videos/progress`,
          {
            userId,
            videoUrl,
            progress,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      });
    },
    [retryApiCall],
  );

  const getVideosProgress = useCallback(
    async (userId: string, courseId: number): Promise<VideoProgress[]> => {
      console.log(`🔵 useVideoService: getVideosProgress called`, { userId, courseId });
      return retryApiCall(async (token) => {
        const response = await axios.get<VideoProgress[]>(
          `${getApiBaseUrl()}/videos/progress/${courseId}?userId=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        return response.data;
      });
    },
    [retryApiCall],
  );

  const getSingleVideosProgress = useCallback(
    async (userId: string, url: string): Promise<VideoProgress[]> => {
      console.log(`🔵 useVideoService: getSingleVideosProgress called`, { userId, url });
      return retryApiCall(async (token) => {
        const encodedVideoUrl = encodeURIComponent(url);
        const response = await axios.get<VideoProgress[]>(
          `${getApiBaseUrl()}/videos/progress?userId=${userId}&url=${encodedVideoUrl}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        return response.data;
      });
    },
    [retryApiCall],
  );

  const fetchVideoDuration = useCallback(
    async (url: string): Promise<{ duration: number }> => {
      console.log(`🔵 useVideoService: fetchVideoDuration called`, { url });
      return retryApiCall(async (token) => {
        const response = await axios.get<{ duration: number }>(
          `${getApiBaseUrl()}/videos/video-duration?url=${encodeURIComponent(url)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        return response.data;
      });
    },
    [retryApiCall],
  );

  return {
    fetchTotalVideoDuration,
    saveVideoProgress,
    getVideosProgress,
    getSingleVideosProgress,
    fetchVideoDuration,
  };
};
